import { trackSession } from '@apis/booking-session.api';
import { useRecoilState } from 'recoil';
import bookingSessionState from '@recoil/atoms/booking/session';
import userState from '../../../../recoil/atoms/auth/user';
import useJwt from '@src/@core/auth/jwt/useJwt';
import useShop from '../../../../utility/hooks/useShop';

export const SessionSteps = {
  NEW_SESSION_STEP: 0,
  CLIENT_TYPE_STEP: 1,
  NO_AVAILABILITY_STEP: 2,
  RETURNING_CLIENT_INFO_STEP: 3,
  RETURNING_CLIENT_VERIFICATION_STEP: 4,
  LOCATION_SELECTION_STEP: 5,
  APPOINTMENT_LANDING_STEP: 6,
  SPECIES_SELECTION_STEP: 7,
  APPOINTMENT_TYPE_SELECTION_STEP: 8,
  PROVIDER_SELECTION_STEP: 9,
  DATE_SELECTION_STEP: 10,
  TIME_SELECTION_STEP: 11,
  CLIENT_INFO_STEP: 12,
  PATIENT_INFO_STEP: 13,
  NEW_CLIENT_VERIFICATION_STEP: 14,
  KEYWORD_ACTIONS_STEP: 15,
  PAYMENT_LOADED_STEP: 16,
  PAYMENT_COLLECTED_STEP: 17,
  APPOINTMENT_BOOKED_STEP: 18,
};

const getDeviceInfo = () => {
  const deviceInfo = {
    // Basic browser info
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    vendor: navigator.vendor,

    // Screen and display info
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    colorDepth: window.screen.colorDepth,
    devicePixelRatio: window.devicePixelRatio,
    orientation: window.screen.orientation?.type,

    // Device capabilities
    touchPoints: navigator.maxTouchPoints,
    cookiesEnabled: navigator.cookieEnabled,
    online: navigator.onLine,
    doNotTrack: navigator.doNotTrack,
    hardwareConcurrency: navigator.hardwareConcurrency,
    deviceMemory: navigator.deviceMemory,

    // Connection info
    connectionType: navigator.connection?.type,
    connectionEffectiveType: navigator.connection?.effectiveType,
    connectionRtt: navigator.connection?.rtt,
    connectionDownlink: navigator.connection?.downlink,

    // Browser features
    localstorage: !!window.localStorage,
    sessionstorage: !!window.sessionStorage,

    // Performance metrics
    timing: window.performance?.timing,
    memory: window.performance?.memory,

    // Viewport info
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,

    // Timezone info
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffset: new Date().getTimezoneOffset(),

    // Geolocation support
    geolocation: !!navigator.geolocation,

    // Browser specific features
    isChrome: /Chrome/.test(navigator.userAgent) && !/Edge/.test(navigator.userAgent),
    isFirefox: /Firefox/.test(navigator.userAgent),
    isSafari: /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
    isEdge: /Edge/.test(navigator.userAgent),
    isIE: /Trident/.test(navigator.userAgent),
    isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  };

  return deviceInfo;
};

export const useTrackStep = () => {
  const [bookingSession, setBookingSession] = useRecoilState(bookingSessionState);
  const [user] = useRecoilState(userState);
  const { jwt } = useJwt();
  const shop = useShop();

  const getRedirect = () => {
    const redirect = new URLSearchParams(location.search).get("redirect");
    if (redirect) return redirect;
    return null;
  };

  const getReferralSource = () => {
    const source = new URLSearchParams(location.search).get("utm_source");
    if (source) {
      const campaign = new URLSearchParams(location.search).get("utm_campaign");
      if (campaign) {
        return `${source}-${campaign}`;
      }
      return source;
    }

    if (document.referrer) {
      try {
        const referrerUrl = new URL(document.referrer);
        return referrerUrl.hostname;
      } catch (error) {
        // If URL parsing fails, return the raw referrer
        return document.referrer;
      }
    }

    return null;
  };

  const trackStep = (step, { appointmentId, referralSource } = {}) => {
    if (!jwt.getClientId() || !shop || jwt.getClientId() === 'login') return;
    if (getRedirect()) return;
    if (location.pathname.includes('/pets')) return;
    try {
      const deviceInfo = getDeviceInfo();
      trackSession({
        session_id: bookingSession.sessionId,
        last_recorded_step: step,
        device_platform: deviceInfo.platform,
        device_cookies_allowed: deviceInfo.cookiesEnabled,
        device_user_agent: deviceInfo.userAgent,
        device_language: deviceInfo.language,
        device_timezone: deviceInfo.timezone,
        device_display_width: deviceInfo.viewportWidth,
        device_display_height: deviceInfo.viewportHeight,
        user_id: user?.id,
        appointment_id: appointmentId,
        referral_source: referralSource || getReferralSource(),
      });
    } catch (error) {}
  };

  return trackStep;
};
